import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { Item, SummonerSpell } from "@outplayed/tooltips";
import { getRoleLabel } from "@ugg/shared/utils/role-helpers";
import { useGlobal } from "reactn";

interface CommonProps extends RoleRewardsProps {
  className?: string;
}

interface RoleRewardsProps {
  champID: number;
  questCompletionTime?: [number];
  role: string;
}

const msToMinutes = (ms: number): string => {
  return (ms / 60000).toFixed(2);
};

export default function RoleRewards(props: RoleRewardsProps) {
  const { role, champID, questCompletionTime } = props;
  const { getChampionImg, getChampionName, getChampionSplashImg } = getRiotAssetsContext();
  const [language] = useGlobal("language");

  const roleQuestText: Record<string, JSX.Element> = {
    top: (
      <div>
        Completing this quest will grant: flat amount of experience,{" "}
        <span className="font-bold text-[#FFF]">increased experience gain</span>, and increased{" "}
        <span className="font-bold text-[#FFF]">level cap of up to 20</span>. It will also grant an{" "}
        <span className="font-bold text-[#FFF]">
          upgraded version of <SummonerSpell className="inline h-[16px] w-[16px]" spellId={12} language={language} ssr webp />{" "}
          Teleport
        </span>{" "}
        or if it wasn't selected as a Summoner Spell it will grant Teleport as a third Summoner Spell.
      </div>
    ),
    jungle: (
      <div>
        Completing this quest will grant:{" "}
        <span className="font-bold text-[#FFF]">
          upgraded smite <SummonerSpell className="inline h-[16px] w-[16px]" spellId={11} language={language} ssr webp />{" "}
        </span>
        , increased <span className="font-bold text-[#FFF]">move speed</span> in jungle or river, and increased rewards from large
        monsters.
      </div>
    ),
    mid: (
      <div>
        Completing this quest will grant a free{" "}
        <Item className="inline-block rounded-[3px] overflow-hidden" itemId={3173} spriteSize={16} isSprite ssr patch={"16.1"} />{" "}
        <span className="font-bold text-[#FFF]">tier 3 boot upgrade</span>, as well as an{" "}
        <Item className="inline-block rounded-[3px] overflow-hidden" itemId={2002} spriteSize={16} isSprite ssr patch={"16.1"} />{" "}
        <span className="font-bold text-[#FFF]">empowered 4-second recall</span> with a cooldown.
      </div>
    ),
    adc: (
      <div>
        Completing this quest will grant: flat amount of gold,{" "}
        <span className="font-bold text-[#FFF]">increased gold gained</span> from minions and champion takedowns, and an effective{" "}
        <span className="font-bold text-[#FFF]">7th item slot</span>.
      </div>
    ),
    support: (
      <div>
        Completing this quest will grant: <span className="font-bold text-[#FFF]">upgraded support item</span>, control ward cost
        reduction, dedicated{" "}
        <Item className="inline-block rounded-[3px] overflow-hidden" itemId={2055} spriteSize={16} isSprite ssr patch={"16.1"} />{" "}
        <span className="font-bold text-[#FFF]">control ward slot</span>.
      </div>
    ),
  };

  return (
    <div className="relative w-full h-full">
      <img
        className="absolute inset-0 bg-cover bg-center object-cover object-top w-full h-full opacity-50"
        src={getChampionSplashImg(champID)}
      />
      <div className="absolute inset-0 from-[#191937] to-[#191937]/50 bg-gradient-to-r"></div>

      <div className="relative z-10 p-[18px]">
        <div className="relative flex items-center w-full mb-[18px]">
          <span className="absolute left-0 w-[2px] h-[20px] rounded-[2px] bg-[#3273fa]"></span>
          <div className="font-inter text-[14px] font-bold text-[#FFF] px-[12px]">Role Rewards</div>
          <div className="absolute right-0 rounded-[3px] border border-[#5C463B] bg-[#11112A] shadow-[0_2px_4px_rgba(0,0,0,0.25)] ">
            <div className="flex items-center font-inter text-[11px] font-medium text-[#CDDCFE]">
              <div className="left-0 w-[27px] h-[27px] overflow-hidden rounded-[3px]">
                <img
                  src={getChampionImg(champID, { webp: true })}
                  alt={getChampionName(champID)}
                  className="w-full h-full object-cover transform scale-[1.2]"
                />
              </div>
              <div className="py-[6px] px-[12px]">
                {getChampionName(champID)} {getRoleLabel(role).toLowerCase()} quest completion goal:{" "}
                {questCompletionTime?.[0] !== undefined && (
                  <span className="font-inter text-[11px] font-bold text-[#FFF]">{msToMinutes(questCompletionTime[0])} min</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full md:max-w-[360px] text-[#CDDCFE] font-inter text-[12px] leading-5 font-medium">
          {roleQuestText[role]}
        </div>
      </div>
    </div>
  );
}
